import { useEffect, useState } from 'react';
import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * The accent families the console ships, in the order `/settings` lists them:
 * the brand red first, then the spectrum, then no hue at all.
 *
 * Only the ids live here. The colours — four per preset, times two themes —
 * live once, in `styles/tokens.css`, and the picker paints each swatch with the
 * preset's own `--accent-light` (spec §32). Adding a preset means adding an id
 * here, a `[data-accent]` block there, and a label.
 */
export const ACCENTS = [
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'ink',
] as const;
export type AccentId = (typeof ACCENTS)[number];

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  accent: AccentId;
  setAccent: (accent: AccentId) => void;
}

const STORAGE_KEY = 'bsod.theme.mode';
const ACCENT_STORAGE_KEY = 'bsod.theme.accent';

function loadInitial(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function loadInitialAccent(): AccentId {
  if (typeof window === 'undefined') return 'red';
  const v = window.localStorage.getItem(ACCENT_STORAGE_KEY);
  return ACCENTS.includes(v as AccentId) ? (v as AccentId) : 'red';
}

export const useThemeStore = create<ThemeState>((set) => ({
  mode: loadInitial(),
  setMode: (mode) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, mode);
    set({ mode });
  },
  // The default is on `<html>` itself (`:root` in tokens.css), so "red" is the
  // preset you get by having made no choice — but it is still written to the
  // attribute, so the page is never a special case.
  accent: loadInitialAccent(),
  setAccent: (accent) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(ACCENT_STORAGE_KEY, accent);
    set({ accent });
  },
}));

export function resolveEffectiveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * The theme currently in effect, as a concrete light/dark value.
 *
 * This is the only place that resolves `system`: the ThemeProvider pins it on
 * `<html class="dark">` and the sonner Toaster needs it as a prop, and both
 * have to agree. Subscribes to the OS preference so a "system" user follows
 * their machine without a reload.
 */
export function useEffectiveTheme(): 'light' | 'dark' {
  const mode = useThemeStore((s) => s.mode);
  const [effective, setEffective] = useState<'light' | 'dark'>(() => resolveEffectiveTheme(mode));

  useEffect(() => {
    setEffective(resolveEffectiveTheme(mode));
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setEffective(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  return effective;
}
